import type { InsuranceCustomerType } from '@/general/src/insuranceCustomerType/shared.js'
import { getInsuranceProviderTitle, type InsuranceProvider } from '@/general/src/insuranceProvider/shared.js'
import { personalKaskoAlfaGeneral } from '@/general/src/insuranceScenario/personalKasko/personalKaskoAlfa/shared.js'
import { personalKaskoIngosGeneral } from '@/general/src/insuranceScenario/personalKasko/personalKaskoIngos/shared.js'
import { personalOsagoAlfaGeneral } from '@/general/src/insuranceScenario/personalOsago/personalOsagoAlfa/shared.js'
import { personalOsagoIngosGeneral } from '@/general/src/insuranceScenario/personalOsago/personalOsagoIngos/shared.js'
import { zPersonalOsagoInsuranceScenarioResult } from '@/general/src/insuranceScenario/personalOsago/shared.js'
import type { InsuranceType } from '@/general/src/insuranceType/shared.js'
import { checkEnumEq } from '@/general/src/other/checkEnumEq.js'
import { getConstKeys } from '@/general/src/other/getConstKeys.js'
import type { InsuranceScenarioKey as InsuranceScenarioKeyPrisma } from '@/backend/src/services/other/prisma.js'
import { z } from 'zod'

export const insuranceScenariosGenerals = [
  personalOsagoAlfaGeneral,
  personalKaskoAlfaGeneral,
  personalOsagoIngosGeneral,
  personalKaskoIngosGeneral,
]

export const insuranceScenarioStatusMap = {
  inactive: 'Выключен',
  initializing: 'Инициализация',
  running: 'Запущен',
  finished: 'Завершен',
  failed: 'Ошибка',
  cancelled: 'Отменён',
} as const
export const insuranceScenarioStatuses = getConstKeys(insuranceScenarioStatusMap)
export const zInsuranceScenarioStatus = z.enum(insuranceScenarioStatuses)
export type InsuranceScenarioStatus = (typeof insuranceScenarioStatuses)[number]
export const getInsuranceScenarioStatusTitle = ({ status }: { status: InsuranceScenarioStatus }) => {
  return insuranceScenarioStatusMap[status]
}

export const personalOsagoInsuranceScenariosKeys = ['personalOsagoAlfa', 'personalOsagoIngos'] as const
export type PersonalOsagoInsuranceScenarioKey = (typeof personalOsagoInsuranceScenariosKeys)[number]
export const personalKaskoInsuranceScenariosKeys = ['personalKaskoAlfa', 'personalKaskoIngos'] as const
export type PersonalKaskoInsuranceScenarioKey = (typeof personalKaskoInsuranceScenariosKeys)[number]
export const insuranceScenariosKeys = [
  ...personalOsagoInsuranceScenariosKeys,
  ...personalKaskoInsuranceScenariosKeys,
] as const
export type InsuranceScenarioKey = (typeof insuranceScenariosKeys)[number]
checkEnumEq<InsuranceScenarioKey, InsuranceScenarioKeyPrisma, true>()
export const zInsuranceScenarioKey = z.enum(insuranceScenariosKeys)
export type InsuranceScenarioGeneral<TInsuranceType extends InsuranceType = InsuranceType> = {
  title: string
  insuranceProvider: InsuranceProvider
  insuranceType: TInsuranceType
  insuranceCustomerType: InsuranceCustomerType
  insuranceScenarioKey: InsuranceScenarioKey
}

export const getInsuranceScenarioKeysOptions = ({ insuranceType }: { insuranceType: InsuranceType }) => {
  return insuranceScenariosGenerals
    .filter((insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceType === insuranceType)
    .map((insuranceScenarioGeneral) => ({
      value: insuranceScenarioGeneral.insuranceScenarioKey,
      label: insuranceScenarioGeneral.title,
    }))
}

export const getInsuranceScenarioKeysOptionsWithInsuranceProviderLabel = ({
  insuranceType,
}: {
  insuranceType: InsuranceType
}) => {
  return insuranceScenariosGenerals
    .filter((insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceType === insuranceType)
    .map((insuranceScenarioGeneral) => ({
      value: insuranceScenarioGeneral.insuranceScenarioKey,
      label: getInsuranceProviderTitle(insuranceScenarioGeneral.insuranceProvider),
    }))
}

export const getInsuranceScenarioGeneral = ({
  insuranceScenarioKey,
}: {
  insuranceScenarioKey: InsuranceScenarioKey
}): InsuranceScenarioGeneral => {
  return insuranceScenariosGenerals.find(
    (insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceScenarioKey === insuranceScenarioKey
  ) as InsuranceScenarioGeneral
}

export const getInsuranceScenarioGeneralOrNull = ({
  insuranceScenarioKey,
}: {
  insuranceScenarioKey: string
}): InsuranceScenarioGeneral | null => {
  return insuranceScenariosGenerals.find(
    (insuranceScenarioGeneral) => insuranceScenarioGeneral.insuranceScenarioKey === insuranceScenarioKey
  ) as InsuranceScenarioGeneral | null
}

export const getInsuranceScenarioTitle = ({ insuranceScenarioKey }: { insuranceScenarioKey: InsuranceScenarioKey }) => {
  return getInsuranceScenarioGeneral({ insuranceScenarioKey }).title
}

export const getInsuranceScenarioTitleOrUnknown = ({ insuranceScenarioKey }: { insuranceScenarioKey: string }) => {
  return getInsuranceScenarioGeneralOrNull({ insuranceScenarioKey })?.title || 'Неизвестный сценарий'
}

export const getInsuranceProviderTitleByInsuranceScenarioKey = ({
  insuranceScenarioKey,
}: {
  insuranceScenarioKey: InsuranceScenarioKey
}) => {
  const insuranceProvider = getInsuranceScenarioGeneral({ insuranceScenarioKey }).insuranceProvider
  return getInsuranceProviderTitle(insuranceProvider)
}

export const isInsuraceScenarioKeySuitableToInsuranceType = ({
  insuranceScenarioKey,
  insuranceType,
}: {
  insuranceScenarioKey: InsuranceScenarioKey
  insuranceType: InsuranceType
}) => {
  const insuranceScenarioGeneral = getInsuranceScenarioGeneral({ insuranceScenarioKey })
  return insuranceScenarioGeneral.insuranceType === insuranceType
}

export const isInsuraceScenarioKeysSuitableToInsuranceType = ({
  insuranceScenariosKeys,
  insuranceType,
}: {
  insuranceScenariosKeys: InsuranceScenarioKey[]
  insuranceType: InsuranceType
}) => {
  return insuranceScenariosKeys.every((insuranceScenarioKey) =>
    isInsuraceScenarioKeySuitableToInsuranceType({ insuranceScenarioKey, insuranceType })
  )
}

export const filterSuitableInsuranceScenariosKeysByInsuranceType = ({
  insuranceType,
  insuranceScenarioKeys,
}: {
  insuranceType: InsuranceType
  insuranceScenarioKeys: InsuranceScenarioKey[]
}) => {
  return insuranceScenarioKeys.filter((insuranceScenarioKey) =>
    isInsuraceScenarioKeySuitableToInsuranceType({ insuranceScenarioKey, insuranceType })
  )
}

export const getInsuranceTypeByInsuranceScenarioKey = ({
  insuranceScenarioKey,
}: {
  insuranceScenarioKey: InsuranceScenarioKey
}) => {
  return getInsuranceScenarioGeneral({ insuranceScenarioKey }).insuranceType
}

export const getInsuranceProviderByInsuranceScenarioKey = ({
  insuranceScenarioKey,
}: {
  insuranceScenarioKey: InsuranceScenarioKey
}) => {
  return getInsuranceScenarioGeneral({ insuranceScenarioKey }).insuranceProvider
}

export const getInsuranceProvidersByInsuranceScenariosKeys = ({
  insuranceScenariosKeys,
}: {
  insuranceScenariosKeys: InsuranceScenarioKey[]
}) => {
  return [
    ...new Set(
      insuranceScenariosKeys.map((insuranceScenarioKey) =>
        getInsuranceProviderByInsuranceScenarioKey({ insuranceScenarioKey })
      )
    ),
  ]
}

export const getInsuranceScenarioResultZSchema = ({ insuranceType }: { insuranceType: InsuranceType }) => {
  return {
    personalOsago: zPersonalOsagoInsuranceScenarioResult,
    // TODO: fix for kasko
    personalKasko: zPersonalOsagoInsuranceScenarioResult,
  }[insuranceType]
}
